import type { RecordModel } from 'pocketbase';
import type { TenantRole } from '@kioskforge/shared';
import { pb } from './pocketbase';

export type Session = {
  user: { id: string; name: string; email: string };
  tenant: { id: string; name: string };
  role: TenantRole;
};

type UserRecord = RecordModel & {
  name?: string;
  email?: string;
  tenant?: string;
  role?: TenantRole;
  expand?: { tenant?: { name?: string } };
};

/**
 * Map a PocketBase user record to the app session shape. The tenant display
 * name comes from `expand.tenant`; a page reload may restore a record without
 * the expansion, so the provider resolves the name separately.
 */
export function toSession(record: UserRecord): Session {
  const tenantId = String(record.tenant ?? '');
  return {
    user: { id: record.id, name: record.name ?? record.email ?? record.id, email: record.email ?? '' },
    tenant: { id: tenantId, name: record.expand?.tenant?.name ?? tenantId },
    role: (record.role ?? 'viewer') as TenantRole
  };
}

export function currentSession(): Session | null {
  if (!pb.authStore.isValid || !pb.authStore.model) return null;
  return toSession(pb.authStore.model as UserRecord);
}

export async function signInWithPassword(email: string, password: string): Promise<Session> {
  const auth = await pb.collection('users').authWithPassword(email, password, { expand: 'tenant' });
  return toSession(auth.record as UserRecord);
}

export function signOut(): void {
  pb.authStore.clear();
}

export async function requestPasswordReset(email: string): Promise<void> {
  await pb.collection('users').requestPasswordReset(email);
}

/**
 * Completes password reset / invite acceptance: PocketBase returns a one-time
 * token by email; the user sets their password with it and joins the tenant
 * with the role the inviter assigned.
 */
export async function confirmPasswordReset(token: string, password: string, passwordConfirm: string): Promise<void> {
  await pb.collection('users').confirmPasswordReset(token, password, passwordConfirm);
}
